import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { AnalyticsSnippet } from '@segment/analytics-next';

import 'core-js/stable';

import { addAmplitudeToConfigHeader } from './resources/amplitude/addAmplitudeConfigToHeader';
import { initGoogleMaps } from './utils/external-libs/googleMaps';
import { initSegment } from './utils/external-libs/segment';
import { setupSentry } from './utils/external-libs/sentry';
import lazyWithRetry, { isIOSSafari } from './utils/lazyWithRetry';

const HomeOwnerFlowWidget = lazyWithRetry(() => import('./components/InitApp/HomeOwnerFlowWidget'));
const LeadFormInit = lazyWithRetry(() => import('../leadForm/LeadFormInit'));

// Proactive chunk preloading for iOS Safari
const preloadChunksForIOSSafari = () => {
  if (isIOSSafari()) {
    // eslint-disable-next-line no-restricted-properties
    const currentUrl = window.location.href;

    // Preload based on current route
    if (currentUrl.includes('/book/')) {
      // Preload HomeOwnerFlowWidget chunk
      import('./components/InitApp/HomeOwnerFlowWidget').catch(() => {
        // Ignore preload errors, the actual lazy load will handle them
      });
    } else if (currentUrl.includes('/lead-form/')) {
      // Preload LeadFormInit chunk
      import('../leadForm/LeadFormInit').catch(() => {
        // Ignore preload errors, the actual lazy load will handle them
      });
    }
  }
};

declare global {
  interface Window {
    analytics: AnalyticsSnippet;
    GOOGLE_RESERVE?: {
      conversion_tracking_url: string;
      partner_id: string;
    };
    SENTRY?: {
      enabled: boolean;
      environment: string;
    };
    SEGMENT?: {
      enabled: boolean;
      jsKey: string;
      cdnEndpointUrl: string;
      trackingEndpointUrl: string;
    };
    GOOGLE_MAPS_API_KEY: string;
    AMPLITUDE_ENDPOINT_URL?: string;
    BOOK_HOUSECALL_URL?: string;
    Cypress: unknown;

    HC_ENV_REVISION: string;
    HC_ENV_NODE_ENV: string;
    HC_ENV_APP_ENVIRONMENT: string;
    RECAPTCHA_ENTERPRISE_KEY: string;
    HOUSECALL_SERVER_URL: string;
    STRIPE_PUBLISHABLE_KEY: string;
    GOOGLE_MAPS_APP_ID: string;
  }
}

window.BOOK_HOUSECALL_URL = BOOK_HOUSECALL_URL;
window.AMPLITUDE_ENDPOINT_URL = AMPLITUDE_ENDPOINT_URL;
window.GOOGLE_MAPS_API_KEY = GOOGLE_MAPS_API_KEY;
window.SENTRY = SENTRY;
window.SEGMENT = SEGMENT;
window.RECAPTCHA_ENTERPRISE_KEY = RECAPTCHA_ENTERPRISE_KEY;
window.HOUSECALL_SERVER_URL = HOUSECALL_SERVER_URL;
window.STRIPE_PUBLISHABLE_KEY = STRIPE_PUBLISHABLE_KEY;
window.GOOGLE_RESERVE = GOOGLE_RESERVE;
window.GOOGLE_MAPS_APP_ID = GOOGLE_MAPS_APP_ID;

const shouldRenderLeadCapture = () => {
  // eslint-disable-next-line no-restricted-properties
  return window.location.href.search('/book/') === -1 && window.location.href.search('/lead-form/') !== -1;
};

const mountComponents = () => {
  const root = document.getElementById('root');
  ReactDOM.render(
    <Suspense fallback={<div />}>
      <HomeOwnerFlowWidget />
    </Suspense>,
    root
  );
};

const mountLeadFormComponents = () => {
  const root = document.getElementById('root');
  ReactDOM.render(
    <Suspense fallback={<div />}>
      <LeadFormInit />
    </Suspense>,
    root
  );
};

const initNewApp = () => {
  initSegment();
  initGoogleMaps();
  addAmplitudeToConfigHeader();
  mountComponents();
};

const initLeadCapture = () => {
  initSegment();
  initGoogleMaps();
  addAmplitudeToConfigHeader();
  mountLeadFormComponents();
};

(function startOLB() {
  // Initialize Sentry FIRST, before any components render
  setupSentry();

  // Preload chunks for iOS Safari before anything else
  preloadChunksForIOSSafari();

  if (shouldRenderLeadCapture()) {
    initLeadCapture();
  } else {
    initNewApp();
  }
})();
